import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  MapPin, User, Clock, Camera, ArrowRight,
  Trash2, Edit2, Building2, UserPlus, ChevronDown, X, RotateCcw, PauseCircle, Play,
} from 'lucide-react';
import { StatusBadge, PriorityBadge } from './StatusBadge';
import { Button } from '../ui/Button';
import { Select, Textarea } from '../ui/Input';
import { updateJobStatus, updateJob, deleteJob, restoreJob, resumeJob } from '../../api/jobs';
import { getFieldStaff } from '../../api/users';
import { STATUS_LABELS, PAUSE_REASON_LABELS, formatJobNumber } from '../../utils/constants';
import { STATUS_TRANSITIONS } from '../../utils/statusTransitions';
import { useAuth } from '../../context/AuthContext';
import { JobStatusTimeline } from './JobStatusTimeline';

export function JobDetail({ job, onClose, onEdit, onRefresh }) {
  const { user } = useAuth();
  const isAdmin = ['admin', 'supervisor'].includes(user?.role);

  // Durum güncelleme
  const [updating, setUpdating]       = useState(false);
  const [newStatus, setNewStatus]     = useState('');
  const [note, setNote]               = useState('');
  const [statusError, setStatusError] = useState('');

  // Restore (Geri Yükle) modal state'i — mode seçimi ve confirmation için
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [restoreMode, setRestoreMode] = useState('reassign'); // 'reassign' | 'unassign'
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState('');

  // Paused job actions (Sprint C.2): resume + cancel from the Beklemede card
  const [resuming, setResuming] = useState(false);
  const [resumeError, setResumeError] = useState('');
  const [cancellingPaused, setCancellingPaused] = useState(false);
  const [cancelPausedError, setCancelPausedError] = useState('');

  // Personel atama/değiştirme
  const [assignOpen, setAssignOpen]       = useState(false);
  const [staffList, setStaffList]         = useState([]);
  const [staffLoading, setStaffLoading]   = useState(false);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [assigning, setAssigning]         = useState(false);
  const [assignError, setAssignError]     = useState('');

  const allTransitions = STATUS_TRANSITIONS[job.status] || [];
  // Status dropdown filters:
  //   - 'approved' only for admin/supervisor (existing rule)
  //   - 'paused' is mobile-only (Sprint C.3); web doesn't expose pause here
  //   - resume (paused -> assigned) goes through the dedicated "Devam Et"
  //     button on the Beklemede card so resumed_at is set; PATCH /status
  //     would skip that.
  const allowed = allTransitions
    .filter(s => s !== 'approved' || isAdmin)
    .filter(s => s !== 'paused')
    .filter(s => !(s === 'assigned' && job.status === 'paused'));

  // Değiştir / Ata butonuna tıklayınca saha personelini yükle
  const openAssign = async () => {
    setAssignOpen(true);
    setAssignError('');
    setSelectedStaff('');
    if (staffList.length === 0) {
      setStaffLoading(true);
      try {
        const d = await getFieldStaff();
        setStaffList(d.users);
      } finally {
        setStaffLoading(false);
      }
    }
  };

  const cancelAssign = () => {
    setAssignOpen(false);
    setSelectedStaff('');
    setAssignError('');
  };

  const handleAssign = async () => {
    if (!selectedStaff) return;
    setAssigning(true);
    setAssignError('');
    try {
      await updateJob(job.id, { assigned_to: selectedStaff });
      setAssignOpen(false);
      onRefresh();
    } catch (err) {
      setAssignError(err.response?.data?.error || 'Atama yapılamadı');
    } finally {
      setAssigning(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;
    setUpdating(true);
    setStatusError('');
    try {
      await updateJobStatus(job.id, { status: newStatus, note });
      onRefresh();
      setNewStatus(''); setNote('');
    } catch (err) {
      setStatusError(err.response?.data?.error || 'Durum güncellenemedi');
    } finally {
      setUpdating(false);
    }
  };

  // Restore: dialog'da seçilen mode ile API çağrısı
  const handleRestore = async () => {
    setRestoring(true);
    setRestoreError('');
    try {
      await restoreJob(job.id, { mode: restoreMode });
      setShowRestoreDialog(false);
      setRestoreMode('reassign'); // dialog kapanırken default'a sıfırla
      onRefresh();
    } catch (err) {
      setRestoreError(err.response?.data?.error || 'Geri yüklenemedi');
    } finally {
      setRestoring(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Bu işi silmek istediğinizden emin misiniz?')) return;
    await deleteJob(job.id);
    onClose();
    onRefresh();
  };

  // Paused -> assigned via dedicated endpoint (sets resumed_at + history note)
  const handleResume = async () => {
    setResuming(true);
    setResumeError('');
    try {
      await resumeJob(job.id);
      onRefresh();
    } catch (err) {
      setResumeError(err.response?.data?.error || 'Devam ettirme başarısız oldu');
    } finally {
      setResuming(false);
    }
  };

  // Paused -> cancelled. Reachable only from this card; PATCH /status path
  // is also valid but admin lands here when the field staff put it on hold.
  const handleCancelPaused = async () => {
    if (!window.confirm('Bu beklemedeki işi iptal etmek istediğinizden emin misiniz?')) return;
    setCancellingPaused(true);
    setCancelPausedError('');
    try {
      await updateJobStatus(job.id, { status: 'cancelled' });
      onRefresh();
    } catch (err) {
      setCancelPausedError(err.response?.data?.error || 'İptal başarısız oldu');
    } finally {
      setCancellingPaused(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Başlık + Rozetler */}
      <div>
        {job.job_number && (
          <span className="text-xs font-mono text-slate-400 mb-1 block">{formatJobNumber(job.job_number)}</span>
        )}
        <div className="flex flex-wrap gap-2 mb-2">
          <StatusBadge status={job.status} />
          <PriorityBadge priority={job.priority} />
        </div>
        <p className="text-slate-600 text-sm mt-1">{job.description || 'Açıklama yok'}</p>
      </div>

      {/* Beklemede (paused) bilgi kartı + aksiyonlar */}
      {job.status === 'paused' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2 text-amber-800 font-semibold">
            <PauseCircle size={18} />
            Beklemede
          </div>

          {job.pause_reason && (
            <div className="text-sm">
              <span className="text-amber-700 font-medium">Sebep:</span>{' '}
              <span className="text-amber-900">
                {PAUSE_REASON_LABELS[job.pause_reason] || job.pause_reason}
              </span>
            </div>
          )}

          {job.pause_note && (
            <div className="text-sm">
              <span className="text-amber-700 font-medium">Not:</span>{' '}
              <span className="text-amber-900">{job.pause_note}</span>
            </div>
          )}

          {job.paused_at && (
            <div className="text-xs text-amber-700">
              Beklemeye alındı: {format(new Date(job.paused_at), 'd MMM HH:mm', { locale: tr })}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              size="sm"
              onClick={handleResume}
              disabled={resuming || cancellingPaused}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              <Play size={13} />
              {resuming ? 'Devam ettiriliyor...' : 'Devam Et'}
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={handleCancelPaused}
              disabled={resuming || cancellingPaused}
            >
              <X size={13} />
              {cancellingPaused ? 'İptal ediliyor...' : 'İptal Et'}
            </Button>
          </div>

          {resumeError && (
            <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{resumeError}</div>
          )}
          {cancelPausedError && (
            <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{cancelPausedError}</div>
          )}
        </div>
      )}

      {/* Bilgi Satırları */}
      <div className="bg-slate-50 rounded-xl p-4 space-y-3 text-sm">
        {job.customer_name && (
          <Row icon={<Building2 size={15} className="text-purple-500" />} label="Müşteri" value={job.customer_name} />
        )}

        {/* Personel — interaktif (sadece admin) */}
        {isAdmin ? (
          <div className="flex items-start gap-2">
            <User size={15} className="text-blue-500 mt-0.5 shrink-0" />
            <span className="text-slate-500 w-20 shrink-0">Personel</span>
            <div className="flex-1 min-w-0">
              {!assignOpen ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-slate-800 font-medium">
                    {job.assigned_to_name || <span className="text-slate-400 font-normal">Atanmamış</span>}
                  </span>
                  {!['completed', 'cancelled'].includes(job.status) && (
                    <button
                      onClick={openAssign}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {job.assigned_to_name
                        ? <><ChevronDown size={12} /> Değiştir</>
                        : <><UserPlus size={12} /> Ata</>}
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {staffLoading ? (
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin" />
                      Yükleniyor...
                    </div>
                  ) : (
                    <Select value={selectedStaff} onChange={e => setSelectedStaff(e.target.value)}>
                      <option value="">— Personel seçin —</option>
                      {staffList.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </Select>
                  )}
                  {assignError && (
                    <p className="text-xs text-red-500">{assignError}</p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleAssign}
                      loading={assigning}
                      disabled={!selectedStaff || staffLoading}
                    >
                      <UserPlus size={13} /> Ata
                    </Button>
                    <Button size="sm" variant="ghost" onClick={cancelAssign}>
                      <X size={13} /> İptal
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          job.assigned_to_name && (
            <Row icon={<User size={15} className="text-blue-500" />} label="Personel" value={job.assigned_to_name} />
          )
        )}

        {job.address && (
          <Row icon={<MapPin size={15} className="text-red-500" />} label="Adres" value={job.address} />
        )}
        <Row
          icon={<MapPin size={15} className="text-slate-400" />}
          label="Koordinat"
          value={`${parseFloat(job.latitude).toFixed(5)}, ${parseFloat(job.longitude).toFixed(5)}`}
        />
        {job.scheduled_at && (
          <Row icon={<Clock size={15} className="text-yellow-500" />} label="Planlandı"
            value={format(new Date(job.scheduled_at), 'd MMMM yyyy HH:mm', { locale: tr })} />
        )}
        {job.started_at && (
          <Row icon={<Clock size={15} className="text-orange-500" />} label="Başladı"
            value={format(new Date(job.started_at), 'd MMMM yyyy HH:mm', { locale: tr })} />
        )}
        {job.completed_at && (
          <Row icon={<Clock size={15} className="text-green-500" />} label="Tamamlandı"
            value={format(new Date(job.completed_at), 'd MMMM yyyy HH:mm', { locale: tr })} />
        )}
      </div>

      {/* Durum Güncelle */}
      {allowed.length > 0 && (
        <div className="border rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-slate-700">Durum Güncelle</p>
          <Select value={newStatus} onChange={e => setNewStatus(e.target.value)}>
            <option value="">— Durum seçin —</option>
            {allowed.map(s => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </Select>
          <Textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Not (isteğe bağlı)"
            rows={2}
          />
          {statusError && (
            <p className="text-xs text-red-500 bg-red-50 rounded-lg px-3 py-2">{statusError}</p>
          )}
          <Button
            onClick={handleStatusUpdate}
            loading={updating}
            disabled={!newStatus}
            className="w-full justify-center"
          >
            <ArrowRight size={15} />
            Güncelle
          </Button>
        </div>
      )}

      {/* Durum Geçmişi — Sprint 2.10 GÖREV 3: JobStatusTimeline'a extract + enhance
          (user_name + STATUS_DOT renkli dot + boş durum mesajı) */}
      <JobStatusTimeline statusHistory={job.status_history} />

      {/* Fotoğraflar */}
      {job.photos?.length > 0 && (
        <div>
          <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
            <Camera size={14} />
            Fotoğraflar ({job.photos.length})
          </p>
          <div className="grid grid-cols-3 gap-2">
            {job.photos.map(p => (
              <a key={p.id} href={p.file_url} target="_blank" rel="noreferrer">
                <img src={p.file_url} alt="" className="rounded-lg object-cover aspect-square w-full hover:opacity-90 transition" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Admin Aksiyonlar */}
      {isAdmin && (
        <div className="flex gap-2 pt-2 border-t">
          <Button variant="secondary" onClick={() => onEdit(job)} className="flex-1 justify-center">
            <Edit2 size={14} />
            Düzenle
          </Button>
          {job.status === 'cancelled' && (
            <Button
              variant="primary"
              onClick={() => setShowRestoreDialog(true)}
              className="flex-1 justify-center"
            >
              <RotateCcw size={14} />
              Geri Yükle
            </Button>
          )}
          {job.status !== 'started' && job.status !== 'on_the_way' && (
            <Button variant="danger" onClick={handleDelete} className="flex-1 justify-center">
              <Trash2 size={14} />
              Sil
            </Button>
          )}
        </div>
      )}

      {/* Restore (Geri Yükle) Dialog */}
      {showRestoreDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">İşi Geri Yükle</h3>
              <p className="text-sm text-slate-600 mt-1">
                Bu işi nasıl geri yüklemek istiyorsunuz?
              </p>
            </div>

            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                <input
                  type="radio"
                  name="restoreMode"
                  value="reassign"
                  checked={restoreMode === 'reassign'}
                  onChange={e => setRestoreMode(e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900">
                    Aynı personele geri ata
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Durum: <span className="font-mono">Atandı</span> · Personel korunur
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                <input
                  type="radio"
                  name="restoreMode"
                  value="unassign"
                  checked={restoreMode === 'unassign'}
                  onChange={e => setRestoreMode(e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900">
                    Bekleyenlere al
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Durum: <span className="font-mono">Bekliyor</span> · Personel sonra atanır
                  </div>
                </div>
              </label>
            </div>

            {restoreError && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {restoreError}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowRestoreDialog(false);
                  setRestoreMode('reassign');
                  setRestoreError('');
                }}
                disabled={restoring}
                className="flex-1 justify-center"
              >
                İptal
              </Button>
              <Button
                variant="primary"
                onClick={handleRestore}
                disabled={restoring}
                className="flex-1 justify-center"
              >
                {restoring ? 'Geri yükleniyor...' : 'Geri Yükle'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      {icon}
      <span className="text-slate-500 w-20 shrink-0">{label}</span>
      <span className="text-slate-800 font-medium">{value}</span>
    </div>
  );
}
